import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useTenant } from '../contexts/TenantContext';
import { getBrands, getCategories, getMedicineTypes, searchProducts } from '../services/pharmacyApi';
import { getAds } from '../services/adApi';
import AdBanner from '../components/AdBanner';
import Pagination from '../components/Pagination';

const discount = (price, mrp) => Math.round(((mrp - price) / mrp) * 100);

const PRICE_RANGES = [
  { label: 'Any price',    min: null, max: null },
  { label: 'Under ₹50',   min: null, max: 50   },
  { label: '₹50 – ₹150',  min: 50,   max: 150  },
  { label: '₹150 – ₹350', min: 150,  max: 350  },
  { label: '₹350+',       min: 350,  max: null },
];

const SORT_OPTIONS = [
  { value: 'relevance',  label: 'Relevance'         },
  { value: 'price-asc',  label: 'Price: Low → High' },
  { value: 'price-desc', label: 'Price: High → Low' },
  { value: 'discount',   label: 'Best Discount'     },
];

const MEDICINE_TYPES = getMedicineTypes();

/* ── ProductCard ──────────────────────────────────────────────────── */
const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart, items } = useCart();
  const [flashAdded, setFlashAdded] = useState(false);

  const pct      = discount(product.price, product.mrp);
  const cartItem = items.find(i => i.id === product.id);
  const inCart   = !!cartItem;

  const handleCart = (e) => {
    e.stopPropagation();
    if (!product.inStock) return;
    addToCart(product);
    setFlashAdded(true);
    setTimeout(() => setFlashAdded(false), 1500);
  };

  return (
    <div className="product-card" onClick={() => navigate(`/services/pharmacy/${product.id}`)} style={{ cursor: 'pointer' }}>
      {product.prescription && (
        <span className="badge-rx" style={{ position: 'absolute', top: '14px', right: '14px' }}>Rx</span>
      )}

      <div className="product-header">
        <div className="emoji-box">{product.emoji}</div>
        <div className="flex-1-min0">
          <div className="product-name">{product.name}</div>
          <div className="product-brand">
            {product.brand}{product.unit ? ` · ${product.unit}` : ''}
          </div>
        </div>
      </div>

      {product.tags.length > 0 && (
        <div className="flex-tags">
          {product.tags.slice(0, 3).map(tag => <span key={tag} className="tag">{tag}</span>)}
        </div>
      )}

      {product.prescription && (
        <div className="strip-info strip-info-product">
          <span>📋</span>
          <span className="rx-pharmacist">Prescription required at checkout</span>
        </div>
      )}

      <div className="price-row">
        <div>
          <span className="t-price">₹{product.price}</span>
          {pct > 0 && (
            <>
              <span className="product-mrp">₹{product.mrp}</span>
              <span className="product-off">{pct}% off</span>
            </>
          )}
        </div>

        {!product.inStock ? (
          <span className="product-oos">Out of stock</span>
        ) : flashAdded ? (
          <button disabled className="product-cart-btn-added">✓ Added</button>
        ) : inCart ? (
          <button className="product-cart-btn-incart" onClick={(e) => { e.stopPropagation(); navigate('/cart'); }}>
            In Cart ({cartItem.quantity})
          </button>
        ) : (
          <button className="btn-primary-sm" onClick={handleCart}
            style={{ background: product.prescription ? 'linear-gradient(135deg, #dc2626, #b91c1c)' : undefined }}>
            {product.prescription ? '+ Add Rx' : '+ Cart'}
          </button>
        )}
      </div>
    </div>
  );
};

/* ── SkeletonCard ─────────────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="card flex-col-gap12">
    {[56, 36, 24, 20, 28].map((h, i) => (
      <div key={i} className="skeleton"
        style={{ height: `${h}px`, width: i === 1 ? '70%' : i === 2 ? '55%' : '90%' }}/>
    ))}
  </div>
);

/* ── Pharmacy ─────────────────────────────────────────────────────── */
const Pharmacy = () => {
  const navigate   = useNavigate();
  const { tenant } = useTenant();


  const [categories,     setCategories] = useState([]);
  const [brands,         setBrands]     = useState([]);
  const [products,       setProducts]   = useState([]);
  const [totalPages,     setTotalPages] = useState(1);
  const [loading,        setLoading]    = useState(true);
  const [error,          setError]      = useState(null);
  const [ads,            setAds]        = useState([]);
  const [adsLoading,     setAdsLoading] = useState(true);

  // Filters
  const [query,          setQuery]        = useState('');
  const [debouncedQ,     setDebouncedQ]   = useState('');
  const [medicineType,   setMedicineType] = useState('all');
  const [activeCategory, setCategory]     = useState('all');
  const [activeBrand,    setBrand]        = useState('');
  const [priceRange,     setPriceRange]   = useState(0);
  const [prescription,   setPrescription] = useState('all');
  const [sort,           setSort]         = useState('relevance');
  const [page,           setPage]         = useState(0);
  const debounceRef = useRef(null);

  // Reload categories + brands when tenant, category, or medicine type changes
  useEffect(() => {
    if (!tenant?.id) return;
    Promise.all([
      getCategories(tenant.id),
      getBrands(tenant.id, { categoryId: activeCategory, medicineType }),
    ]).then(([cats, brnds]) => {
      setCategories(cats);
      setBrands(brnds);
    }).catch(() => {});
  }, [tenant?.id, activeCategory, medicineType]);

  // Debounce search query
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(query), 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Fetch ads
  useEffect(() => {
    if (!tenant?.id) return;
    setAdsLoading(true);
    getAds(tenant.id, 'PHARMACY_PAGE')
      .then(setAds)
      .finally(() => setAdsLoading(false));
  }, [tenant?.id]);

  // Reset to page 0 whenever a filter (other than page itself) changes
  useEffect(() => { setPage(0); }, [activeCategory, medicineType, activeBrand, priceRange, debouncedQ, sort]);

  // Server-side fetch — categoryId, medicineType, brand, price range, search
  // and sort (price-asc/price-desc) are all applied on the backend, since the
  // catalog (~124k products) is far too large to filter in the browser.
  // "discount" sort and the "prescription" toggle are applied client-side
  // below, on just the current page of results.
  useEffect(() => {
    if (!tenant?.id) return;
    const range = PRICE_RANGES[priceRange];
    setLoading(true);
    setError(null);
    searchProducts(tenant.id, {
      categoryId:   activeCategory,
      medicineType: medicineType !== 'all' ? medicineType : undefined,
      brand:        activeBrand || undefined,
      minPrice:     range.min ?? undefined,
      maxPrice:     range.max ?? undefined,
      q:            debouncedQ || undefined,
      sort:         sort === 'discount' ? 'relevance' : sort,
      page,
      size: 24,
    })
      .then(({ items, totalPages: tp }) => {
        setProducts(items);
        setTotalPages(tp);
        setLoading(false);
      })
      .catch(() => { setError('Could not load products.'); setLoading(false); });
  }, [tenant?.id, activeCategory, medicineType, activeBrand, priceRange, debouncedQ, sort, page]);

  // Client-side, applied only to the current page: prescription toggle + discount sort
  const sorted = useMemo(() => {
    let arr = [...products];

    if (prescription === 'otc') arr = arr.filter(p => !p.prescription);
    if (prescription === 'rx')  arr = arr.filter(p =>  p.prescription);

    if (sort === 'discount') arr.sort((a, b) => discount(b.price, b.mrp) - discount(a.price, a.mrp));

    return arr;
  }, [products, prescription, sort]);

  const handleMedicineType = (typeId) => {
    setMedicineType(typeId);
    setCategory('all');
    setBrand('');
  };

  const handleCategory = (catId) => {
    setCategory(catId);
    setBrand('');
  };

  const resetFilters = () => {
    setQuery('');
    setMedicineType('all');
    setCategory('all');
    setBrand('');
    setPriceRange(0);
    setPrescription('all');
    setSort('relevance');
    setPage(0);
  };

  const hasActiveFilters = medicineType !== 'all' || activeCategory !== 'all'
    || activeBrand || priceRange !== 0 || prescription !== 'all' || debouncedQ;

  if (!tenant) return (
    <div className="page">
      <main className="page-main">
        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Pharmacy</span>
        </div>
        <div className="fade-up pharmacy-head">
          <h1 className="t-h1">💊 Pharmacy</h1>
          <p className="t-body-sm">Select a tenant from the top bar to browse products.</p>
        </div>
      </main>
    </div>
  );

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Pharmacy</span>
        </div>

        <div className="fade-up pharmacy-head">
          <h1 className="t-h1">💊 Pharmacy</h1>
          <p className="t-body-sm">Order medicines, health products, and more — delivered to your door.</p>
        </div>

        {/* ── Ad / Campaign Banners ── */}
        <div className="fade-up">
          <AdBanner ads={ads} loading={adsLoading} />
        </div>

        <div className="chip-row fade-up-1">
          {MEDICINE_TYPES.map(type => (
            <button
              key={type.id}
              className={`chip${medicineType === type.id ? ' active' : ''}`}
              onClick={() => handleMedicineType(type.id)}
            >
              <span>{type.icon}</span><span>{type.label}</span>
            </button>
          ))}
        </div>

        <div className="search-wrap fade-up-2">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input className="search-input" type="text" value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search medicines, brands, conditions…" />
          {query && <button className="search-clear" onClick={() => setQuery('')}>×</button>}
        </div>

        {categories.length > 0 && (
          <div className="chip-row fade-up-3">
            {categories.map(cat => (
              <button key={cat.id} className={`chip${activeCategory === cat.id ? ' active' : ''}`}
                onClick={() => handleCategory(cat.id)}>
                <span>{cat.icon}</span><span>{cat.label}</span>
              </button>
            ))}
          </div>
        )}

        <div className="filter-bar fade-up-3">
          <select className="select-filter" value={activeBrand} onChange={e => setBrand(e.target.value)}
            style={{ color: activeBrand ? '#1e1b4b' : '#9ca3af' }}>
            <option value="">All Brands</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>

          <select className="select-filter" value={priceRange} onChange={e => setPriceRange(Number(e.target.value))}
            style={{ color: priceRange ? '#1e1b4b' : '#9ca3af' }}>
            {PRICE_RANGES.map((r, i) => <option key={i} value={i}>{r.label}</option>)}
          </select>

          <div className="toggle-group">
            {[['all','All'], ['otc','OTC'], ['rx','Rx only']].map(([val, label]) => (
              <button key={val} className={`toggle-item${prescription === val ? ' active' : ''}`}
                onClick={() => setPrescription(val)}>{label}</button>
            ))}
          </div>

          <div className="flex-spacer" />

          {hasActiveFilters && (
            <button className="btn-clear" onClick={resetFilters}>Reset filters</button>
          )}

          <select className="select-filter" value={sort} onChange={e => setSort(e.target.value)}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {error ? (
          <div className="error-state"><span>⚠️</span> {error}</div>
        ) : (
          <>
            {!loading && (
              <div className="t-body-sm results-count">
                {sorted.length === 0
                  ? 'No products found'
                  : `${sorted.length} product${sorted.length !== 1 ? 's' : ''} on this page${totalPages > 1 ? ` · page ${page + 1} of ${totalPages}` : ''}`}
              </div>
            )}

            <div className="product-grid">
              {loading
                ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
                : sorted.length === 0
                  ? (
                    hasActiveFilters ? (
                      <div className="no-results">
                        <div className="no-results-icon">🔍</div>
                        <p className="no-results-title">No products match your filters</p>
                        <button className="t-link t-link-noresult" onClick={resetFilters}>
                          Clear all filters →
                        </button>
                      </div>
                    ) : (
                      <div className="no-results">
                        <div className="no-results-icon">🏪</div>
                        <p className="no-results-title">Our pharmacy catalog is being updated</p>
                        <p className="t-body-sm" style={{ marginTop: 4, color: '#9ca3af' }}>
                          Please check back shortly — we're stocking up.
                        </p>
                      </div>
                    )
                  )
                  : sorted.map((product, i) => (
                      <div key={product.id} style={{ animation: `fadeUp .35s ${Math.min(i, 8) * 40}ms ease both` }}>
                        <ProductCard product={product} />
                      </div>
                    ))
              }
            </div>

            {!loading && totalPages > 1 && (
              <div className="pagination-row" style={{ marginTop: 20 }}>
                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </>
        )}

      </main>
    </div>
  );
};

export default Pharmacy;